import { useEffect, useMemo, useState } from 'react'
import { Search, Star, LayoutGrid, List } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead, Tabs, Empty, Rows } from '../components/UI'
import { MarketTable, CoinIcon, Sparkline } from '../components/Market'
import { useAuth, useTickers, useMarketReady, toast } from '../store'
import { get, post } from '../lib/api'
import { clsx, num, pct, changeClass } from '../lib/format'
import { Link } from 'react-router-dom'
import { useSeo } from '../lib/seo'

export default function Markets() {
  useSeo({
    title: 'قیمت لحظه‌ای ارزهای دیجیتال',
    description: 'قیمت لحظه‌ای بیت‌کوین، اتریوم، تتر و بیش از ۲۰ ارز دیجیتال به تومان، همراه با نمودار و تغییرات ۲۴ ساعته.',
    keywords: 'قیمت بیت کوین، قیمت تتر، قیمت ارز دیجیتال، بازار رمزارز',
    canonical: '/markets',
  })
  const { user } = useAuth()
  const [quote, setQuote] = useState('IRT')
  const [q, setQ] = useState('')
  const [view, setView] = useState<'table' | 'grid'>('table')
  const [favs, setFavs] = useState<string[]>([])
  const [onlyFav, setOnlyFav] = useState(false)

  // اشتراک مستقیم به داده بازار (نه به تابع list) تا با هر به‌روزرسانی قیمت رندر شود
  const all = useTickers(quote === 'FAV' ? undefined : quote)
  const ready = useMarketReady()

  useEffect(() => {
    if (user) get<string[]>('/market/favorites').then(setFavs).catch(() => {})
  }, [user])

  const toggleFav = async (symbol: string) => {
    if (!user) return toast.err('برای افزودن به علاقه‌مندی‌ها وارد حساب شوید')
    try {
      const r = await post<{ favorite: boolean }>(`/market/favorites/${symbol}`)
      setFavs((f) => (r.favorite ? [...f, symbol] : f.filter((s) => s !== symbol)))
    } catch (e: any) {
      toast.err(e.message)
    }
  }

  const rows = useMemo(() => {
    let r = all
    if (quote === 'FAV' || onlyFav) r = r.filter((t) => favs.includes(t.symbol))
    if (q.trim()) {
      const s = q.trim().toLowerCase()
      r = r.filter((t) => t.symbol.toLowerCase().includes(s) || t.faName.includes(s) || t.enName.toLowerCase().includes(s))
    }
    return r
  }, [all, quote, q, favs, onlyFav])

  return (
    <Shell>
      <SectionHead title="بازارها" subtitle="قیمت لحظه‌ای تمام ارزهای دیجیتال قابل معامله در ارزینو" />

      <Card className="mb-5 !p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs
            value={quote}
            onChange={setQuote}
            size="sm"
            items={[
              { key: 'IRT', label: 'تومان' },
              { key: 'USDT', label: 'تتر' },
              { key: 'FAV', label: <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" />علاقه‌مندی</span> },
            ]}
          />
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              className="input !py-2.5 pr-10"
              placeholder="جستجوی ارز (مثلاً بیت‌کوین یا BTC)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="hidden gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1 sm:flex">
            {([['table', List], ['grid', LayoutGrid]] as const).map(([k, Icon]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={clsx('rounded-lg p-2 transition', view === k ? 'bg-brand-500/20 text-brand-200' : 'text-slate-500 hover:text-slate-300')}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
      </Card>

      {rows.length === 0 ? (
        <Card>{ready ? <Empty text="بازاری با این مشخصات پیدا نشد" /> : <Rows n={8} />}</Card>
      ) : view === 'table' ? (
        <Card className="!p-2">
          <MarketTable rows={rows} favorites={favs} onToggleFav={toggleFav} />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {rows.map((t) => (
            <Link key={t.symbol} to={`/trade/${t.symbol}`}>
              <Card hover className="h-full">
                <div className="mb-4 flex items-center gap-3">
                  <CoinIcon symbol={t.base} color={t.color} />
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-white">{t.faName}</div>
                    <div className="num text-xs text-slate-500" dir="ltr">{t.base}/{t.quote === 'IRT' ? 'TMN' : t.quote}</div>
                  </div>
                  <span className={clsx('num mr-auto text-sm font-bold', changeClass(t.change))}>{pct(t.change)}</span>
                </div>
                <div className="num mb-3 text-xl font-extrabold text-white">{num(t.last)}</div>
                <Sparkline data={t.sparkline} up={t.change >= 0} w={230} h={44} />
              </Card>
            </Link>
          ))}
        </div>
      )}
    </Shell>
  )
}
